import { DescriptionStatement, IDescriptionStatement } from './DescriptionStatement';
import { ICoreProduct, IProductData } from '~api/interfaces/IProduct';
import { IMedia, Media } from './Media';
import { IProductFinish, ProductFinish } from './ProductFinish';
import { IProductReview, ProductReview } from './ProductReview';
import { action, computed, observable } from 'mobx';

import { Benefit } from './Benefit';
import { Category } from './Category';
import { Coverage } from './Coverage';
import { CrossSell } from './CrossSell';
import { DEFAULT_IMAGE_ATTRIBUTE } from '../../constants/DefaultImageAttribute';
import { IAsset } from './Asset';
import { IKeyValue } from './BasicEntities';
import { IPriceRangeData } from './Price';
import { InventoryStatus } from '../../constants/InventoryStatuses';
import { PRODUCT_USAGE_TYPES } from '../../constants/ProductUsageTypes';
import { ProductBrush } from './ProductBrush';
import { ProductShadeGroup } from './ProductShadeGroup';
import { ProductSkin } from './ProductSkin';
import { ProductUsage } from './ProductUsage';
import { Shade } from './Shade';
import { Size } from './Size';
import { Sku } from './Sku';
import { Tag } from './Tag';
import { UrlFormatter } from '@estee/elc-formatters';
import { copyPropertiesExcludeNullsAndGetters } from '../../utils/ObjectUtils';
import { decodeHtml } from '../../utils/ContentFormatter';
import { diContainer } from '../../../service-setup/diContainer';
import { isSkuDisplayable } from '../../utils/IsSkuDisplayable';
import serviceNames from '~setup/ProductService';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

interface ProductFromState extends Omit<Product, 'tags' | 'reviews'> {
    tags: Tag[];
    reviews: IProductReview;
}

export interface IShadesByShadeGroup {
    productShadeGroup: ProductShadeGroup;
    shades: Shade[];
}

export class Product {
    @observable public brand: string = '';
    @observable public defaultCategory: Category;
    @observable public description: string = '';
    @observable public displayName: string = '';
    @observable public displayNameDecoded: string = '';
    @observable public subDisplayName: string = '';
    @observable public isHazmat: boolean;
    @observable public seoMetaDescription: string = '';
    @observable public seoMetaKeywords: string = '';
    @observable public productBadge: string = '';
    @observable public productBadgeImage: string = '';
    @observable public productId: string = '';
    @observable public productUrl: string = '';
    @observable public shortDescription: string = '';
    @observable public subHeader: string = '';
    @observable public media: Media;
    @observable public initialSkus: Sku[] = [];
    @observable public tags: { items: Tag[] };
    @observable public crossSell: CrossSell[] = [];
    @observable public usage: ProductUsage[] = [];
    @observable public benefit: Benefit;
    @observable public keyIngredient: string = '';
    @observable public reviews: ProductReview;
    @observable public collections: IKeyValue[] = [];
    @observable public skinConcern: IKeyValue[] = [];
    @observable public fragranceFamily: IKeyValue[] = [];
    @observable public finish?: IProductFinish[];
    @observable public ilnFormulatedWithout?: string = '';
    @observable public productFinish: ProductFinish[] = [];
    @observable public form: IKeyValue[] = [];
    @observable public coverage: Coverage[] = [];
    @observable public skinGoal: IKeyValue[] = [];
    @observable public maincat?: IKeyValue[] = [];
    @observable public subcat?: IKeyValue[] = [];
    @observable public style?: IKeyValue[] = [];
    @observable public mood?: IKeyValue[] = [];
    @observable public scent?: IKeyValue[] = [];
    @observable public productQuantity: number = 1;
    @observable public freeFrom: IKeyValue[] = [];
    @observable public descriptionStatements: IDescriptionStatement[] = [];
    @observable public skin: ProductSkin;
    @observable public shadeGroups: ProductShadeGroup[];
    @observable public occasion: IKeyValue[] = [];
    @observable public fragranceNotes: IKeyValue[] = [];
    @observable public keyIngredients: IKeyValue[] = [];
    @observable public usageOptions: IKeyValue[] = [];
    @observable public brush: ProductBrush;
    @observable public filteredSkus: Sku[];
    @observable public simulatedSkus: Sku[];
    @observable public timeOfUse: IKeyValue[] = [];
    @observable public fragranceKeyNotes: string = '';

    static urlFormatter: UrlFormatter;

    public constructor() {
        Product.urlFormatter = diContainer.get(serviceNames.urlFormatter);
    }

    @computed
    public get quantity() {
        return this.productQuantity;
    }

    @action
    public setQuantity(quantity: number) {
        this.productQuantity = quantity;
    }

    @action
    public setSkus(skus: Sku[]) {
        this.initialSkus = skus;
    }

    @action setFilteredSkus(skus: Sku[]) {
        this.filteredSkus = skus;
    }

    @computed
    public get skus() {
        return this.filteredSkus?.length > 0 ? this.filteredSkus : this.initialSkus;
    }

    @computed
    private get skusWithShades() {
        return this.skus.filter((sku) => sku.shades && sku.shades.length);
    }

    @computed
    private get skusWithSizes() {
        return this.skus.filter((sku) => sku.sizes && sku.sizes.length);
    }

    @computed
    private get skusWithSkinTypes() {
        return this.skus.filter((sku) => sku.skin && sku.skin.type && sku.skin.type.length);
    }

    @computed
    private get skusWithPrice() {
        return this.skus.filter((sku) => sku.priceObject);
    }

    @computed
    private get skusWithLoyaltyDiscount() {
        return this.skus.filter((sku) => sku.loyaltyDiscountPrice);
    }

    @computed
    private get availableSkus() {
        return this.skus.filter(this.isActiveDefaultSku);
    }

    @computed
    private get firstSku() {
        return this.skus[0];
    }

    @computed
    public get isOutOfStock() {
        return this.skus.every((sku) => sku.inventoryStatus === InventoryStatus.TempOutOfStock);
    }

    @computed
    public get isSoldOut() {
        return this.skus.every((sku) => sku.inventoryStatus === InventoryStatus.SoldOut);
    }

    @computed
    public get isTemporarlyOutOfStock() {
        return this.isOutOfStock && this.skus.some((sku) => sku.isShoppable);
    }

    @computed
    public get shades() {
        return this.skusWithShades.reduce((acc: Shade[], sku: Sku) => {
            const { shades = [] } = sku;
            const { length: shadesLength = 0, 0: firstShade } = shades;
            const shadeToBeAdded = shadesLength > 1 ? Shade.formattedShades(shades) : firstShade;
            shadeToBeAdded.parentSku = sku;
            const shouldAddShade =
                shadeToBeAdded.name && !acc.some((shade) => shade.name === shadeToBeAdded.name);

            return shouldAddShade ? [...acc, shadeToBeAdded] : acc;
        }, []);
    }

    @computed
    public get perfectShades() {
        return this.shades.filter((shade) => shade.isPerfectShade);
    }

    @computed
    public get sizes() {
        const sizes: Size[] = [];

        this.skusWithSizes.forEach((sku: Sku) => {
            const sizeToBeAdded =
                sku.sizes.length > 1 ? Size.formattedSizes(sku.sizes) : sku.sizes[0];
            sizeToBeAdded.parentSku = sku;

            if (!sizes.some((size) => size.value === sizeToBeAdded.value)) {
                sizes.push(sizeToBeAdded);
            }
        });

        return sizes;
    }

    public getShadeByName(name: string) {
        return this.shades.find((shade) => shade.name === name);
    }

    public getSizeByLabel(label: string) {
        return this.sizes.find((size) => size.value === label);
    }

    @computed
    public get isShaded() {
        return this.shades.length > 0;
    }

    @computed
    public get hasMultipleShades() {
        return this.shades.length > 1;
    }

    @computed
    public get skusCount() {
        return this.skus.length;
    }

    @computed
    public get isSized() {
        return this.sizes.length > 0;
    }

    @computed
    public get hasMultipleSizes() {
        return this.sizes.length > 1;
    }

    @computed
    public get defaultOriginalPrice() {
        return this.defaultSku.originalPrice || 0;
    }

    @computed
    public get defaultPrice() {
        return this.defaultSku.priceObject && this.defaultSku.priceObject.isDiscounted
            ? this.defaultSku.priceObject.price
            : this.defaultOriginalPrice;
    }

    @computed
    public get defaultFormattedPrice() {
        return this.defaultSku.priceObject && this.defaultSku.priceObject.isDiscounted
            ? this.defaultSku.priceObject.priceFormatted
            : '';
    }

    @computed
    public get defaultFormattedOriginalPrice() {
        return this.defaultSku.priceObject
            ? this.defaultSku.priceObject.originalPriceFormatted
            : '';
    }

    @computed
    public get defaultSize() {
        if (this.defaultSku.sizes.length === 0) {
            return '';
        }

        return this.defaultSku.sizes[0].value;
    }

    @computed
    public get defaultImage(): IAsset {
        return this.defaultSku.defaultMediumImage;
    }

    @computed
    public get skuPromoMessage() {
        return this.defaultSku.skuPromoMessaging;
    }

    @computed
    public get largeImages(): IAsset[] {
        const media: IMedia = this.media;
        if (media === undefined) {
            return [DEFAULT_IMAGE_ATTRIBUTE];
        }

        return media.large;
    }

    @computed
    public get mediumImages(): IAsset[] {
        const media: IMedia = this.media;
        if (media === undefined) {
            return [DEFAULT_IMAGE_ATTRIBUTE];
        }

        return media.medium;
    }

    @computed
    public get benefitStatement() {
        return this.benefit && this.benefit.statement ? this.benefit.statement : '';
    }

    @computed
    public get finishStatement() {
        return this.productFinish ? this.productFinish.map((finish) => finish.name).join(',') : '';
    }

    @computed
    public get finishKeys() {
        return this.productFinish ? this.productFinish.map((finish) => finish.key) : [];
    }

    @computed
    public get skinConcernKeys() {
        return this.skinConcern ? this.skinConcern.map((skin_concern) => skin_concern.key) : [];
    }

    @computed
    public get coverageKeys() {
        return this.coverage ? this.coverage.map((coverage) => coverage.key) : [];
    }

    // TODO: Revisit our approach below on how we map usage fields per brand
    // These will change once we finalize how to handle brand-specific fields

    @computed
    public get whoItsFor() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.WHO_IT_IS_FOR);
    }

    @computed
    public get whatItIs() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.WHAT_IT_IS);
    }

    @computed
    public get whatItDoes() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.WHAT_IT_DOES);
    }

    @computed
    public get howDoesItWork() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.HOW_DOES_IT_WORK);
    }

    @computed
    public get greatToKnow() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.GREAT_TO_KNOW);
    }

    @computed
    public get howToUse() {
        return this.getUsageFieldByType(PRODUCT_USAGE_TYPES.HOW_TO_USE);
    }

    @computed
    public get hasLargeImages() {
        return this.largeImages.length > 0 && this.largeImages[0].src;
    }

    @computed
    public get hasMediumImages() {
        return this.mediumImages.length > 0 && this.mediumImages[0].src;
    }

    @computed
    public get hasColorExperience() {
        return this.skus.some((sku) => sku.isColorExperience);
    }

    @computed
    public get hasFoundationExperience() {
        return this.skus.some((sku) => sku.isFoundationExperience);
    }

    private getUsageFieldByType(usageType: string) {
        const usageItem = this.usage.find((usage) => usage.type === usageType);
        if (usageItem) {
            return usageItem;
        }

        return {
            label: '',
            content: '',
            type: ''
        };
    }

    @computed
    public get allBenefits() {
        if (!this.benefit || !this.benefit.benefits) {
            return '';
        }

        return this.benefit.benefits.map(({ value }) => value).join(', ');
    }

    @computed
    public get allFinishes() {
        if (!this.productFinish) {
            return '';
        }

        return this.productFinish.map(({ name }) => name).join(', ');
    }

    @computed
    public get allSkinTypes() {
        const skinTypeArrays = this.skusWithSkinTypes.map(({ skin: { type } }) =>
            type.map(({ value }) => value)
        );
        const allSkinTypes = [].concat.apply([], skinTypeArrays);

        return [...new Set(allSkinTypes)].join(', ');
    }

    @computed
    public get skinTypeKeys(): string[] {
        const keys: string[] = [];

        this.skusWithSkinTypes.map(({ skin: { type } }) =>
            type.map(({ key }) => {
                if (key && !keys.includes(key)) {
                    keys.push(key);
                }
            })
        );

        return keys;
    }

    private isActiveDefaultSku = (sku: Sku) => {
        return isSkuDisplayable(sku) || sku.inventoryStatus !== InventoryStatus.TempOutOfStock;
    };

    @computed
    public get defaultSku(): Sku {
        return (
            this.availableSkus.find((sku) => sku.isDefaultSku) ||
            this.availableSkus[0] ||
            this.firstSku
        );
    }

    @action
    public mergeSkus(newSkus: Sku[]) {
        const missingSkus = newSkus.reduce((accumulator: Sku[], currentSku: Sku) => {
            const existingSku = this.skus.find((_sku) => _sku.skuId === currentSku.skuId);

            if (!existingSku) {
                return [...accumulator, currentSku];
            }

            if (existingSku.isDefaultSku !== currentSku.isDefaultSku) {
                existingSku.isDefaultSku = currentSku.isDefaultSku;
            }

            return accumulator;
        }, []);

        this.initialSkus = [...this.skus, ...missingSkus];
    }

    public static fromPlainObject(tempData: Product): Product {
        const product = new Product();
        copyPropertiesExcludeNullsAndGetters(product, tempData);
        const data = tempData as unknown as ProductFromState;

        product.defaultCategory = Category.fromJson(data.defaultCategory);
        product.displayNameDecoded = decodeHtml(data.displayName);
        product.initialSkus = data.skus.map((sku) => {
            const skuModel = Sku.fromPlainObject(sku);
            skuModel.parentProduct = product;

            return skuModel;
        });
        product.benefit = Benefit.fromJson(data.benefit);
        product.media = Media.fromJson(data.media);
        product.tags = { items: data.tags.map((tag) => Tag.fromJson(tag)) };
        product.usage = data.usage.map(ProductUsage.fromJson);
        product.crossSell = data.crossSell.map(CrossSell.fromPlainObject);
        product.reviews = data.reviews && ProductReview.fromJson(data.reviews);
        product.coverage = data.coverage.map(Coverage.fromJson);
        product.skin = data.skin && ProductSkin.fromPlainObject(data.skin);
        product.brush = data.brush && ProductBrush.fromPlainObject(data.brush);

        return product;
    }

    // eslint-disable-next-line complexity
    public static fromJson(data: ICoreProduct): Product {
        const product = new Product();

        copyPropertiesExcludeNullsAndGetters(product, data);

        const productDataMapping = {
            displayName: data.display_name,
            subDisplayName: data.sub_display_name,
            isHazmat: data.is_hazmat,
            seoMetaDescription: data.seo_meta_description,
            seoMetaKeywords: data.seo_meta_keywords,
            productBadge: data.product_badge,
            productBadgeImage: data.product_badge_image,
            productId: data.product_id,
            productUrl: Product.urlFormatter.langPrefixUrl(data.product_url),
            shortDescription: data.short_description,
            description: data.description,
            subHeader: data.sub_header,
            keyIngredient: data.key_ingredient,
            keyIngredients: data.key_ingredients,
            fragranceNotes: data.fragrance_notes,
            fragranceKeyNotes: data.fragrance_key_notes
        };
        copyPropertiesExcludeNullsAndGetters(product, productDataMapping);

        product.defaultCategory = Category.fromJson(data.default_category);
        product.displayNameDecoded = decodeHtml(data.display_name);
        product.benefit = Benefit.fromJson(data.benefit);
        product.media = Media.fromJson(data.media);
        product.tags = {
            items: data.tags?.items ? data.tags.items.map(Tag.fromJson) : []
        };
        product.usage = data.usage ? data.usage.map(ProductUsage.fromJson) : [];
        product.crossSell = data.cross_sell ? data.cross_sell.map(CrossSell.fromJson) : [];
        product.keyIngredient = data.key_ingredient || '';
        product.reviews = data.reviews && ProductReview.fromJson(data.reviews);
        product.productFinish = data.finish || [];
        product.finish = data.finish || [];
        product.coverage = data.coverage ? data.coverage.map(Coverage.fromJson) : [];
        product.descriptionStatements = data.description_statements
            ? data.description_statements
                  .map(DescriptionStatement.fromJson)
                  .filter((item: DescriptionStatement) => item.content)
                  .sort((a, b) => (a.priority > b.priority ? 1 : -1))
            : [];
        product.form = data.form ? data.form : [];
        product.skinConcern = data.skin && data.skin.concern ? data.skin.concern : [];
        product.fragranceFamily = data.fragrance_family ? data.fragrance_family : [];
        product.fragranceKeyNotes = data.fragrance_key_notes || '';
        product.skinGoal = data.skin_goal ? data.skin_goal : [];
        product.collections = data.collection ? data.collection : [];
        product.freeFrom = data.free_from ? data.free_from : [];
        product.skin = data.skin ? ProductSkin.fromJson(data.skin) : new ProductSkin();
        product.shadeGroups = data.shade_groups
            ? data.shade_groups.map(ProductShadeGroup.fromJson)
            : [];
        product.usageOptions = data.usage_options || [];
        product.brush = data.brush ? ProductBrush.fromJson(data.brush) : new ProductBrush();
        product.ilnFormulatedWithout = data.iln_formulated_without || '';
        product.timeOfUse = data.time_of_use ? data.time_of_use : [];

        return product;
    }

    @computed get shadesByShadeGroup(): IShadesByShadeGroup[] {
        return this.shadeGroups.reduce((acc: IShadesByShadeGroup[], productShadeGroup) => {
            let shadesByShadeGroup: Shade[] = [];
            this.skus.forEach(({ shadeGroups, shades }) => {
                const existsInShadeGroup = shadeGroups.some(
                    (skuShadeGroup) => skuShadeGroup.key === productShadeGroup.key
                );

                if (existsInShadeGroup) {
                    shadesByShadeGroup = shadesByShadeGroup.concat(...shades);
                }
            });

            if (shadesByShadeGroup && shadesByShadeGroup.length > 0) {
                acc.push({
                    productShadeGroup,
                    shades: shadesByShadeGroup
                });
            }

            return acc;
        }, []);
    }

    @computed
    public get loyaltyPriceRange(): IPriceRangeData | null {
        const prices = this.skusWithLoyaltyDiscount.reduce(
            (acc, { loyaltyDiscountPrice }) => {
                if (!loyaltyDiscountPrice) {
                    return acc;
                }
                const skuPrice = {
                    price: loyaltyDiscountPrice.price,
                    formatted: loyaltyDiscountPrice.priceFormatted
                };

                const hasLessPrice = skuPrice.price < acc.minPrice;
                const hasGreaterPrice = skuPrice.price > acc.maxPrice;

                return {
                    minPrice: hasLessPrice ? skuPrice.price : acc.minPrice,
                    maxPrice: hasGreaterPrice ? skuPrice.price : acc.maxPrice,
                    formattedMinPrice: hasLessPrice ? skuPrice.formatted : acc.formattedMinPrice,
                    formattedMaxPrice: hasGreaterPrice ? skuPrice.formatted : acc.formattedMaxPrice
                };
            },
            {
                minPrice: Number.POSITIVE_INFINITY,
                maxPrice: Number.NEGATIVE_INFINITY,
                formattedMinPrice: null,
                formattedMaxPrice: null
            }
        );

        const { formattedMinPrice, formattedMaxPrice } = prices;
        if (formattedMinPrice && formattedMaxPrice && formattedMinPrice !== formattedMaxPrice) {
            return { formattedMinPrice, formattedMaxPrice };
        }

        return null;
    }

    @computed
    public get priceRange(): IPriceRangeData | null {
        const prices = this.skusWithPrice.reduce(
            (acc, sku) => {
                if (!sku.priceObject) {
                    return acc;
                }
                const priceObject = sku.priceObject;
                const skuPrice = priceObject.isDiscounted
                    ? { price: priceObject.price, formatted: priceObject.priceFormatted }
                    : {
                          price: priceObject.originalPrice,
                          formatted: priceObject.originalPriceFormatted
                      };
                const hasLessPrice = skuPrice.price < acc.minPrice;
                const hasGreaterPrice = skuPrice.price > acc.maxPrice;
                const hasGreaterDiscountPercent =
                    !!sku.sitewideDiscountPercent &&
                    sku.sitewideDiscountPercent > acc.maxDiscountPercent;

                return {
                    minPrice: hasLessPrice ? skuPrice.price : acc.minPrice,
                    maxPrice: hasGreaterPrice ? skuPrice.price : acc.maxPrice,
                    formattedMinPrice: hasLessPrice ? skuPrice.formatted : acc.formattedMinPrice,
                    formattedMaxPrice: hasGreaterPrice ? skuPrice.formatted : acc.formattedMaxPrice,
                    maxDiscountPercent: hasGreaterDiscountPercent
                        ? sku.sitewideDiscountPercent
                        : acc.maxDiscountPercent
                };
            },
            {
                minPrice: Number.POSITIVE_INFINITY,
                maxPrice: Number.NEGATIVE_INFINITY,
                formattedMinPrice: null,
                formattedMaxPrice: null,
                maxDiscountPercent: 0
            }
        );

        const { maxDiscountPercent, formattedMinPrice, formattedMaxPrice } = prices;
        if (formattedMinPrice && formattedMaxPrice && formattedMinPrice !== formattedMaxPrice) {
            return { maxDiscountPercent, formattedMinPrice, formattedMaxPrice };
        }

        return null;
    }

    @computed
    public get isDiscounted(): boolean {
        return this.skusWithPrice.some((sku) => sku.priceObject?.isDiscounted === true);
    }

    @computed
    public get priceRangeNotDiscounted(): IPriceRangeData | null {
        const prices = this.skusWithPrice.reduce(
            (acc, sku) => {
                if (!sku.priceObject) {
                    return acc;
                }
                const priceObject = sku.priceObject;
                const skuPrice = {
                    price: priceObject.originalPrice,
                    formatted: priceObject.originalPriceFormatted
                };
                const hasLessPrice = skuPrice.price < acc.minPrice;
                const hasGreaterPrice = skuPrice.price > acc.maxPrice;

                return {
                    minPrice: hasLessPrice ? skuPrice.price : acc.minPrice,
                    maxPrice: hasGreaterPrice ? skuPrice.price : acc.maxPrice,
                    formattedMinPrice: hasLessPrice ? skuPrice.formatted : acc.formattedMinPrice,
                    formattedMaxPrice: hasGreaterPrice ? skuPrice.formatted : acc.formattedMaxPrice
                };
            },
            {
                minPrice: Number.POSITIVE_INFINITY,
                maxPrice: Number.NEGATIVE_INFINITY,
                formattedMinPrice: null,
                formattedMaxPrice: null
            }
        );

        const { formattedMinPrice, formattedMaxPrice } = prices;
        if (formattedMinPrice && formattedMaxPrice && formattedMinPrice !== formattedMaxPrice) {
            return { formattedMinPrice, formattedMaxPrice };
        }

        return null;
    }

    @action
    public sortSkus(sortMethod: (s1: Sku, s2: Sku) => number) {
        this.initialSkus = [...this.initialSkus.sort(sortMethod)];
    }

    public toJSON(): IProductData {
        return {
            brand: this.brand,
            crossSell: this.crossSell,
            defaultMediumImage: this.defaultSku.defaultMediumImage,
            defaultLargeImage: this.defaultSku.defaultLargeImage,
            defaultCategory: this.defaultCategory,
            defaultSku: this.defaultSku.toJSON(),
            defaultOriginalPrice: this.defaultOriginalPrice,
            defaultPrice: this.defaultPrice,
            defaultSize: this.defaultSize,
            defaultImage: this.defaultImage,
            description: this.description,
            displayName: this.displayName,
            isHazmat: this.isHazmat,
            ilnFormulatedWithout: this.ilnFormulatedWithout,
            media: this.media,
            productBadge: this.productBadge,
            productId: this.productId,
            productUrl: this.productUrl,
            shades: this.shades.map((shade) => shade.toJSON()),
            sizes: this.sizes.map((size) => size.toJSON()),
            skus: this.skus.map((sku) => sku.toJSON()),
            skuPromoMessage: this.skuPromoMessage,
            subDisplayName: this.subDisplayName,
            seoMetaDescription: this.seoMetaDescription,
            seoMetaKeywords: this.seoMetaKeywords,
            shortDescription: this.shortDescription,
            subHeader: this.subHeader,
            tags: this.tags.items,
            usage: this.usage,
            benefit: this.benefit,
            keyIngredient: this.keyIngredient,
            reviews: this.reviews && this.reviews.toJSON(),
            form: this.form,
            skinConcern: this.skinConcern,
            fragranceFamily: this.fragranceFamily,
            fragranceKeyNotes: this.fragranceKeyNotes,
            skinGoal: this.skinGoal,
            collections: this.collections,
            coverage: this.coverage,
            finish: this.finish,
            freeFrom: this.freeFrom,
            skin: this.skin.toJSON(),
            keyIngredients: this.keyIngredients,
            fragranceNotes: this.fragranceNotes,
            occasion: this.occasion,
            descriptionStatements: this.descriptionStatements,
            usageOptions: this.usageOptions,
            brush: this.brush && this.brush.toJSON(),
            timeOfUse: this.timeOfUse
        };
    }

    public static clone(data: Product): Product {
        const product = new Product();
        copyPropertiesExcludeNullsAndGetters(product, data);

        return product;
    }
}
